import {
  applyProjectChangesToProposal,
  type BuildProposalAppendActionPatchOptions,
} from './apply-changes-to-proposal.helpers';
import { collectRemappedProposalChangesForDuplicateSubtree } from './clone-proposal-changes-for-duplicate-subtree.helpers';
import {
  applyProjectChanges,
  isNonVersionChange,
  type ApplyBatchChangesOptions,
} from '@shared/helpers/pipeline/update-engine.helpers';
import {
  ProjectChangeType,
  type ProjectChange,
  type ProjectChangeMap,
} from '@shared/helpers/pipeline/update-engine.models';
import { ElementPropertyName } from '@shared/models/element-property.model';
import { ElementKind, type Project } from '@shared/models/project.model';
import type { ProposalId } from '@shared/models/proposals.model';
import {
  applyRegenerateIdsOptions,
  createId,
} from '@shared/helpers/id.helpers';
import {
  getAllBuildingVersions,
  getElementById,
} from '@shared/helpers/recursive-element.helpers';
import { isRecipeScopedElementId } from '@shared/helpers/recipe.helpers';
import { reportSharedError } from '@shared/helpers/error-reporting.helpers';

type ElementAddChange = ProjectChangeMap[ProjectChangeType.ElementAdd];
type ElementActivateChange =
  ProjectChangeMap[ProjectChangeType.ElementActivate];

type ProposalScopedElementAddChanges = {
  baselineChange: ElementAddChange;
  proposalChange: ElementActivateChange;
};

/**
 * Non-version {@link ProjectChangeType} values that always mutate baseline project data instead of
 * being stored only on the active proposal's action list (shared across all proposals).
 *
 * Version-scoped changes are excluded by {@link isVersionScopedProjectChange} instead, so this set
 * does not have to duplicate every proposal/action/result version mutator type.
 *
 * Note: {@link ProjectChangeType.ElementRecipeApply} is intentionally **not** in this set.
 * Recipe swaps are proposal-scoped so a marked proposal can change a single element's recipe
 * without affecting the baseline or other proposals (see DEV-3293). Recipe library save/reset
 * updates baseline via {@link applyBaselineRecipeOverwriteOnProject}, not proposal actions.
 */
export const PROJECT_CHANGE_TYPES_EXCLUDED_FROM_PROPOSAL: ReadonlySet<ProjectChangeType> =
  new Set<ProjectChangeType>([
    ProjectChangeType.ElementAdd,
    ProjectChangeType.ElementName,
    ProjectChangeType.ElementRemove,
    ProjectChangeType.ElementPropertyAdd,
    ProjectChangeType.ElementPropertyRemove,
    ProjectChangeType.ElementPropertyRename,
  ]);

const PROPERTY_CHANGE_TYPES_WITH_NAME: ReadonlySet<ProjectChangeType> =
  new Set<ProjectChangeType>([
    ProjectChangeType.ElementPropertyResolved,
    ProjectChangeType.ElementPropertyExpression,
    ProjectChangeType.ElementPropertyFormatted,
    ProjectChangeType.ElementPropertyValue,
    ProjectChangeType.ElementPropertyCount,
    ProjectChangeType.ElementPropertyRename,
    ProjectChangeType.ElementPropertyFallbackCount,
  ]);

/**
 * Whether a change targets the SBEF code property (same value for every proposal; apply on baseline).
 */
const isSbefCodePropertyProjectChange = (change: ProjectChange): boolean => {
  if (!PROPERTY_CHANGE_TYPES_WITH_NAME.has(change.type)) return false;
  // Narrow: only the above types carry `propertyName`
  if (!('propertyName' in change)) return false;
  const { propertyName } = change;
  return propertyName === ElementPropertyName.SbefCode;
};

/**
 * Whether a change targets version-level state and must run through the version update path.
 */
const isVersionScopedProjectChange = (change: ProjectChange): boolean =>
  !isNonVersionChange(change);

/**
 * Builds the paired baseline/proposal changes for a proposal-scoped element add.
 * The element must exist in baseline so other proposals can reference the same tree,
 * but starts deactivated until the selected proposal activates it.
 */
const createProposalScopedElementAddChanges = (
  change: ProjectChange,
  proposalId: ProposalId,
): ProposalScopedElementAddChanges | undefined => {
  if (change.type !== ProjectChangeType.ElementAdd) return undefined;
  if (change.factory.kind !== ElementKind.Element) return undefined;

  const rootRegenerateIds = applyRegenerateIdsOptions(change.regenerateIds, {
    isRoot: true,
    preserveRootElementIds: true,
  });
  const elementId = createId(change.factory, rootRegenerateIds);

  return {
    baselineChange: {
      ...change,
      factory: {
        ...change.factory,
        id: elementId,
        isDeactivated: true,
      },
      createdInProposalId: proposalId,
      regenerateIds: applyRegenerateIdsOptions(change.regenerateIds, {
        preserveRootElementIds: true,
      }),
    },
    proposalChange: {
      type: ProjectChangeType.ElementActivate,
      elementId,
    },
  };
};

/**
 * Reports baseline-routed changes whose target element exists only in the proposal-resolved tree.
 *
 * Recipe children materialized by {@link ProjectChangeType.ElementRecipeApply} have no baseline
 * row, so a change routed to baseline by {@link isProjectChangeExcludedFromProposal} — SBEF code,
 * rename, property add/remove/rename — applies to nothing and the user's edit is lost.
 *
 * Re-routing it to the proposal is not an option: the same predicate is what
 * {@link isValidProposalsAndActions} uses to reject baseline-only changes stored on an action, so
 * the edit would fail validation. The exclusion is deliberate (these types change the tree shape
 * every proposal shares, which for a recipe child is owned by the recipe payload).
 *
 * Report the drop to Sentry via {@link reportSharedError}. The user is not notified: the save
 * still succeeds, and a snackbar would fire on a known limitation rather than a failed persist.
 * {@link ProjectChangeType.ElementRemove} is reported from {@link applyBatchChanges} instead, so a
 * missing remove is not double-counted.
 *
 * Limited to recipe-scoped ids: they can never be produced by an {@link ProjectChangeType.ElementAdd}
 * earlier in the same batch, so a miss here is always a genuine proposal-only target.
 */
const reportBaselineChangesForProposalOnlyElements = (
  project: Project,
  baselineChanges: ProjectChange[],
): void => {
  const dropped = baselineChanges.filter(
    (change) =>
      change.type !== ProjectChangeType.ElementRemove &&
      'elementId' in change &&
      isRecipeScopedElementId(change.elementId) &&
      !getElementById(project, change.elementId, false),
  );

  if (dropped.length === 0) return;

  reportSharedError(
    new Error('Baseline-only changes dropped for proposal-only elements'),
    {
      dropped: dropped.map((change) => ({
        type: change.type,
        elementId: 'elementId' in change ? change.elementId : undefined,
      })),
    },
  );
};

/**
 * Returns the selected proposal id only when it exists in the current {@link Project}.
 * Stale URL params must not route baseline edits into proposal actions.
 */
const resolveSelectedProposalId = (
  project: Project,
  selectedProposalId: ProposalId | undefined,
): ProposalId | undefined => {
  if (!selectedProposalId) return undefined;
  const version = getAllBuildingVersions(project).find((candidate) =>
    candidate.proposals?.some((proposal) => proposal.id === selectedProposalId),
  );
  return version ? selectedProposalId : undefined;
};

/**
 * True when the change must not be recorded as a proposal-only action and should run through
 * {@link applyProjectChanges} even if a non-baseline proposal is selected.
 */
export const isProjectChangeExcludedFromProposal = (
  change: ProjectChange,
): boolean =>
  isVersionScopedProjectChange(change) ||
  PROJECT_CHANGE_TYPES_EXCLUDED_FROM_PROPOSAL.has(change.type) ||
  isSbefCodePropertyProjectChange(change);

export type ProjectChangesProposalPartition = {
  baselineChanges: ProjectChange[];
  proposalChanges: ProjectChange[];
};

/**
 * Splits changes for {@link applyProjectChangesWithProposalContext}: baseline edits vs proposal actions.
 * When there is no selected proposal, every change is treated as baseline (proposal list stays empty).
 */
export const partitionProjectChangesForProposalContext = (
  changes: ProjectChange[],
  selectedProposalId: ProposalId | undefined,
): ProjectChangesProposalPartition => {
  // No active proposal: every change mutates the project tree directly.
  if (!selectedProposalId) {
    return {
      baselineChanges: changes,
      proposalChanges: [],
    };
  }

  const baselineChanges: ProjectChange[] = [];
  const proposalChanges: ProjectChange[] = [];

  for (const change of changes) {
    const elementAddChanges = createProposalScopedElementAddChanges(
      change,
      selectedProposalId,
    );
    if (elementAddChanges) {
      baselineChanges.push(elementAddChanges.baselineChange);
      proposalChanges.push(elementAddChanges.proposalChange);
      continue;
    }

    // Excluded types (e.g. add/remove element, recipe, SBEF code) affect all proposals the same way.
    if (isProjectChangeExcludedFromProposal(change))
      baselineChanges.push(change);
    // Everything else is recorded on the proposal so other proposals stay untouched.
    else proposalChanges.push(change);
  }

  return { baselineChanges, proposalChanges };
};

/**
 * Applies {@link ProjectChange}[] to the project using {@link partitionProjectChangesForProposalContext}
 * to run baseline edits before recording proposal-scoped actions.
 *
 * ## Parity with baseline (important)
 *
 * Applying a change with {@link selectedProposalId} set must show the user the same thing as
 * applying it without one. Read the result back with {@link getResolvedBuildingVersion} for that
 * proposal and it matches the tree a baseline edit produces. Callers can therefore treat the two
 * modes as one: a feature never needs a proposal-specific variant of the same edit, and anything
 * built on the resolved tree (diffs, KPIs, "create baseline from proposal") stays truthful.
 *
 * What legitimately differs:
 * - **The stored tree.** A proposal-scoped change becomes an {@link Action} and leaves
 *   {@link Baseline.elements} untouched, so other proposals keep their own view. Only the
 *   resolved tree carries the change.
 * - **{@link ProjectChangeType.ElementAdd}.** The row is added to baseline deactivated and
 *   activated by the proposal, so every proposal can reference it while only this one shows it.
 * - **Types excluded from proposals.** {@link isProjectChangeExcludedFromProposal} (rename, add or
 *   remove element/property, SBEF code, version-level state) writes to baseline regardless, so the
 *   edit is visible in every proposal.
 *
 * Parity is pinned by `proposal-baseline-parity.helpers.test.ts`, including the differences above.
 * Extend it when adding a proposal-scoped {@link ProjectChangeType}.
 *
 * @param project - Current project snapshot
 * @param selectedProposalId - Active proposal id, if any
 * @param changes - Changes from the user or pipeline
 * @param proposalAppendOptions - Context for appending a proposal action
 * @param options - Forwarded to the baseline apply. `{ replayOfRace: true }`
 *   does not report element removes that are already absent on server truth.
 * @returns Updated project (possibly same reference when there is nothing to apply)
 */
export const applyProjectChangesWithProposalContext = (
  project: Project,
  selectedProposalId: ProposalId | undefined,
  changes: ProjectChange[],
  proposalAppendOptions?: BuildProposalAppendActionPatchOptions,
  options?: ApplyBatchChangesOptions,
): Project => {
  if (changes.length === 0) return project;

  const proposalId = resolveSelectedProposalId(project, selectedProposalId);
  const { baselineChanges, proposalChanges } =
    partitionProjectChangesForProposalContext(changes, proposalId);

  let next = project;
  // Apply baseline edits first so proposal-scoped updates see the updated structure.
  if (baselineChanges.length > 0) {
    reportBaselineChangesForProposalOnlyElements(next, baselineChanges);
    next = applyProjectChanges(next, baselineChanges, options);
  }

  const duplicateSubtreeCloneChanges =
    proposalId && baselineChanges.length > 0
      ? collectRemappedProposalChangesForDuplicateSubtree(
          next,
          proposalId,
          baselineChanges,
        )
      : [];

  if (
    !proposalId ||
    (proposalChanges.length === 0 && duplicateSubtreeCloneChanges.length === 0)
  ) {
    return next;
  }

  // Cloned duplicate subtree edits must run before {@link ProjectChangeType.ElementActivate}
  // on the same element — otherwise {@link condenseChanges} absorbs them into the activate.
  return applyProjectChangesToProposal(
    next,
    proposalId,
    [...duplicateSubtreeCloneChanges, ...proposalChanges],
    proposalAppendOptions,
  );
};
